import { useState } from 'react';
import { Plus, ChevronDown, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useCampaignStore } from '@/stores';
import { AddContributionDialog } from './AddContributionDialog';
import { AddExpenditureDialog } from './AddExpenditureDialog';

/**
 * Header affordance on the Close hub for hand-entering a single transaction
 * without leaving the period view. Reuses the existing entry dialogs verbatim;
 * this component only owns the menu and which dialog is open. The directional
 * item icons signal money-in (contribution) vs money-out (expenditure).
 */
export function AddTransactionMenu() {
  const canDo = useCampaignStore((s) => s.canDo('Finance'));
  const [contributionOpen, setContributionOpen] = useState(false);
  const [expenditureOpen, setExpenditureOpen] = useState(false);

  // Mirror ContributionsPageContent: hide (not disable) the affordance for
  // users without the Finance "do" capability.
  if (!canDo) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline">
            <Plus aria-hidden="true" />
            Add transaction
            <ChevronDown aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        {/* Don't let the menu's close-auto-focus return focus to the trigger; it
            races the dialog's focus trap and can leave the dialog unfocused. */}
        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DropdownMenuItem onClick={() => setContributionOpen(true)}>
            <ArrowDownLeft aria-hidden="true" />
            Add contribution
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setExpenditureOpen(true)}>
            <ArrowUpRight aria-hidden="true" />
            Add expenditure
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mount each dialog only while open: their top-level query hooks (contacts,
          compliance profile) otherwise fire on every Close-hub load. Mirrors the
          lazy-mount InboxStrip uses for TriageModal. */}
      {contributionOpen && (
        <AddContributionDialog open={contributionOpen} onOpenChange={setContributionOpen} />
      )}
      {expenditureOpen && (
        <AddExpenditureDialog open={expenditureOpen} onOpenChange={setExpenditureOpen} />
      )}
    </>
  );
}
